import { useState } from 'react'
import { lumi } from '@/lib/lumi'

// Synchronise la collection publique vehicle_bookings (lecture NO_RESTRICTIONS)
// a partir des donnees sensibles de la collection reservations (CREATOR_ONLY).
// Cette collection publique ne contient AUCUNE donnee personnelle du client,
// uniquement les champs necessaires au calcul de disponibilite.
const syncBookingCreate = async (reservation: any) => {
  try {
    if (!reservation?._id || !reservation?.vehicleId || !reservation?.startDate || !reservation?.endDate) {
      return
    }
    const now = new Date().toISOString()
    await lumi.entities.vehicle_bookings.create({
      vehicleId: String(reservation.vehicleId),
      startDate: reservation.startDate,
      endDate: reservation.endDate,
      status: reservation.status || 'pending',
      reservationId: String(reservation._id),
      createdAt: reservation.createdAt || now,
      updatedAt: reservation.updatedAt || now,
    })
  } catch (error) {
    console.error('Sync vehicle_bookings (create) failed:', error)
  }
}

const syncBookingUpdate = async (reservationId: string, reservation: any) => {
  try {
    const { list } = await lumi.entities.vehicle_bookings.list({
      filter: { reservationId: String(reservationId) },
      limit: 1,
    })
    const now = new Date().toISOString()
    const payload = {
      vehicleId: String(reservation.vehicleId),
      startDate: reservation.startDate,
      endDate: reservation.endDate,
      status: reservation.status || 'pending',
      reservationId: String(reservationId),
      createdAt: reservation.createdAt || now,
      updatedAt: now,
    }

    if (list && list.length > 0) {
      await lumi.entities.vehicle_bookings.update(list[0]._id, payload)
    } else {
      // Fallback : creer l'entree publique si elle n'existait pas encore
      await lumi.entities.vehicle_bookings.create(payload)
    }
  } catch (error) {
    console.error('Sync vehicle_bookings (update) failed:', error)
  }
}

const syncBookingDelete = async (reservationId: string) => {
  try {
    const { list } = await lumi.entities.vehicle_bookings.list({
      filter: { reservationId: String(reservationId) },
      limit: 10,
    })
    if (list && list.length > 0) {
      await Promise.all(
        list.map((b: any) => lumi.entities.vehicle_bookings.delete(b._id))
      )
    }
  } catch (error) {
    console.error('Sync vehicle_bookings (delete) failed:', error)
  }
}

export const useReservations = () => {
  const [reservations, setReservations] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchReservations = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.reservations.list(options || {})
      setReservations(list)
      setTotal(total)
    } catch (error) {
      console.error('Fetch reservations failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const createReservation = async (data: any) => {
    const newReservation = await lumi.entities.reservations.create(data)
    // Synchronise la collection publique pour les controles de disponibilite
    await syncBookingCreate(newReservation)
    await fetchReservations()
    return newReservation
  }

  const updateReservation = async (id: string, updates: any) => {
    if (typeof id !== 'string') {
      throw new Error('ID must be a string')
    }
    const updated = await lumi.entities.reservations.update(id, updates)
    // Synchronise la collection publique avec les nouvelles valeurs
    await syncBookingUpdate(id, { ...updated, ...updates })
    await fetchReservations()
    return updated
  }

  const deleteReservation = async (id: string) => {
    if (typeof id !== 'string') {
      throw new Error('Invalid ID: must be a string')
    }
    await lumi.entities.reservations.delete(id)
    // Supprime aussi l'entree miroir publique
    await syncBookingDelete(id)
    await fetchReservations()
  }

  // Verifie la disponibilite via la collection publique vehicle_bookings
  // (lisible par tous les utilisateurs, contrairement a reservations qui est CREATOR_ONLY)
  const checkAvailability = async (vehicleId: string, startDate: string, endDate: string) => {
    try {
      console.log('Checking availability (public bookings) for:', { vehicleId, startDate, endDate })

      const { list } = await lumi.entities.vehicle_bookings.list({
        filter: {
          vehicleId,
          status: { $in: ['pending', 'confirmed', 'paid', 'active'] },
          $and: [
            { startDate: { $lte: endDate } },
            { endDate: { $gte: startDate } },
          ],
        },
      })

      console.log('Existing public bookings found:', list.length)
      const isAvailable = list.length === 0
      console.log('Vehicle is available:', isAvailable)

      return isAvailable
    } catch (error) {
      console.error('Check availability failed:', error)
      return true
    }
  }

  // Recupere toutes les dates reservees pour un vehicule depuis la collection publique
  const fetchBookedDates = async (vehicleId: string): Promise<string[]> => {
    try {
      const { list } = await lumi.entities.vehicle_bookings.list({
        filter: {
          vehicleId,
          status: { $in: ['pending', 'confirmed', 'paid', 'active'] },
        },
        limit: 500,
      })

      // Genere toutes les dates individuelles entre startDate et endDate pour chaque reservation
      const bookedDates: Set<string> = new Set()
      list.forEach((res: any) => {
        if (!res.startDate || !res.endDate) return
        const start = new Date(res.startDate)
        const end = new Date(res.endDate)
        const current = new Date(start)
        current.setHours(0, 0, 0, 0)
        end.setHours(0, 0, 0, 0)
        while (current <= end) {
          const y = current.getFullYear()
          const m = String(current.getMonth() + 1).padStart(2, '0')
          const d = String(current.getDate()).padStart(2, '0')
          bookedDates.add(`${y}-${m}-${d}`)
          current.setDate(current.getDate() + 1)
        }
      })

      return Array.from(bookedDates)
    } catch (error) {
      console.error('Fetch booked dates failed:', error)
      return []
    }
  }

  return {
    reservations,
    total,
    loading,
    fetchReservations,
    createReservation,
    updateReservation,
    deleteReservation,
    checkAvailability,
    fetchBookedDates,
  }
}
